import React, { useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Input,
  Label,
  Spinner,
  Badge,
} from "reactstrap";
import axios from "axios";
import { useDomainFlow } from "./DomainResellerFlow";
import { SearchResult } from "./types";

const baseUrl = process.env.REACT_APP_BASE_URL;

const DomainSearchStep: React.FC = () => {

  const { state, updateState, nextStep } = useDomainFlow();

  const [keyword, setKeyword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!keyword.trim()) {
      updateState({
        error: "Please enter a keyword to search.",
      });
      return;
    }

    setIsLoading(true);
    updateState({ isLoading: true, searchResults: [], error: null });

    try {
      const requestBody: any = {
        keyword: keyword.trim(),
      };

      const response = await axios.post(
        `${baseUrl}/companyFormation/searchDomains`,
        requestBody,
        { withCredentials: true }
      );

      if (response && response.status === "success") {
        const results = response.results || [];
        // Sort results: purchasable domains first, then unavailable ones
        const sortedResults = [...results].sort((a, b) => {
          if (a.purchasable && !b.purchasable) return -1;
          if (!a.purchasable && b.purchasable) return 1;
          return 0;
        });
        updateState({
          searchResults: sortedResults,
          isLoading: false,
          error: results.length === 0 ? "No domains found for your search." : null,
        });
      } else {
        updateState({
          isLoading: false,
          error: response.data?.error || "Failed to search domains.",
        });
      }
    } catch (error: any) {
      console.error("Domain search error:", error);
      let errorMessage = "An error occurred while searching domains.";
      
      if (error.response) {
        if (error.response.status === 401 || error.response.status === 422) {
          errorMessage = error.response.data?.error || "Authentication error.";
        } else if (error.response.status === 403) {
          errorMessage = "Session expired!";
          setTimeout(() => {
            window.location.href = "/logout";
          }, 2000);
        } else if (error.response.status === 429) {
          errorMessage = "Rate limit exceeded. Please try again later.";
        } else {
          errorMessage = error.response.data?.error || "Failed to search domains.";
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }

      updateState({
        isLoading: false,
        error: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price?: number): string => {
    if (price === undefined || price === null) return "N/A";
    return `$${price.toFixed(2)}`;
  };

  const getPurchaseTypeBadge = (type?: string): string => {
    if (!type) return "";
    const typeMap: Record<string, string> = {
      registration: "New Registration",
      aftermarket_i: "Aftermarket (i)",
      expiring: "Expiring",
      backorder: "Backorder",
      aftermarket_s: "Aftermarket (s)",
      aftermarket_b: "Aftermarket (b)",
    };
    return typeMap[type] || type;
  };

  const handleSelectDomain = (domain: SearchResult) => {
    updateState({ selectedDomain: domain });
    nextStep();
  };

  return (
    <>
      <Row className="justify-content-end">
          <Col md={8} lg={6}>
            <Card className="rounded-4 border">
              <CardBody>
                <h4 className="card-title mb-4">Search for Domain Names</h4>
                <Form onSubmit={handleSearch}>
                  <Row className="align-items-end">
                    <Col md={6}>
                      <Label htmlFor="keyword" className="form-label">
                        Keyword <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="keyword"
                        className="form-control"
                        placeholder="Enter domain name or keyword"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        required
                      />
                    </Col>
                    <Col md={6} className="d-flex gap-2">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isLoading || state.isLoading || !keyword.trim()}
                      >
                        {(isLoading || state.isLoading) ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Searching...
                          </>
                        ) : (
                          "Search Domains"
                        )}
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          setKeyword("");
                          updateState({ searchResults: [], selectedDomain: null });
                        }}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {state.searchResults.length > 0 && (
          <Row className="mt-3">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0">
                  Found {state.searchResults.length} {state.searchResults.length === 1 ? 'domain' : 'domains'}
                </h4>
              </div>
              <Row>
                {state.searchResults.map((result, index) => (
                  <Col key={index} md={6} lg={4} className="mb-2">
                    <Card 
                      className={`rounded-3 border h-100 ${result.purchasable ? 'border-success' : 'border-secondary opacity-75'}`}
                      style={{ transition: 'all 0.3s ease' }}
                    >
                      <CardBody className="p-3 d-flex flex-column">
                        <div className="d-flex align-items-center mb-2">
                          {result.purchasable && (
                            <span className="text-success me-2" style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                              ✓
                            </span>
                          )}
                          <h5 
                            className="mb-0 text-break flex-grow-1" 
                            style={{ 
                              fontSize: '1rem', 
                              fontWeight: 600,
                              color: result.purchasable ? '#1f2937' : '#6b7280'
                            }}
                          >
                            {result.domainName}
                          </h5>
                          {result.premium && (
                            <Badge color="warning" className="ms-2" style={{ fontSize: '0.7rem' }}>
                              Premium
                            </Badge>
                          )}
                        </div>

                        {result.purchasable && (
                          <>
                            <div className="mb-2">
                              <div className="h5 mb-0 text-primary" style={{ fontWeight: 700, fontSize: '1.25rem' }}>
                                {formatPrice(result.purchasePrice)}
                              </div>
                              {result.renewalPrice && (
                                <div className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 300 }}>
                                  Renews at {formatPrice(result.renewalPrice)}
                                </div>
                              )}
                            </div>
                            <Button
                              color="primary"
                              size="sm"
                              className="mt-auto"
                              onClick={() => handleSelectDomain(result)}
                              disabled={state.isLoading}
                              style={{ 
                                borderRadius: '6px',
                                fontWeight: 600
                              }}
                            >
                              {state.isLoading ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Processing...
                                </>
                              ) : (
                                'Select'
                              )}
                            </Button>
                          </>
                        )}

                        {!result.purchasable && (
                          <div className="text-muted mt-auto">
                            <small>Not available</small>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        )}
    </>
  );
};

export default DomainSearchStep;
